import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  Animated,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
  type LayoutChangeEvent,
} from "react-native";
import { useTranslation } from "react-i18next";
import type { EffortScale } from "@heraion/shared";

import type { UiTheme } from "../theme";
import { useReducedMotion } from "../useReducedMotion";
import { EffortTick } from "./EffortTick";
import { MeterRow, METER_ROW_MIN_HEIGHT } from "./MeterRow";
import {
  EFFORT_TICK_WIDTH,
  WHEEL_RIR,
  effortWheelOffset,
} from "./effortWheel";

// THE effort meter (AP-0, Decision 8): completing a set IS the effort
// judgment. One easy→hard WHEEL carrying every canonical value, painted on
// the theme's monochrome `effortRamp` — plus a separate Fail button OUTSIDE
// the continuum (failing to move the bar is a different event, not one more
// step, so it sits apart with a visible gap, in a NEUTRAL box — not the
// ramp, and not an alarm: a fail is a normal outcome, not an error). One
// tap = the completion gesture; the host
// (logger, AP-0b) owns what the tap triggers (completedAt + PR check +
// rest timer + effort save).
//
// ⭐ LG-63 turned the five fixed segments into an eleven-tick wheel: the band
// was the ONLY surface in the app speaking 0..4 while everything else spoke
// 0..10, so a prescribed RIR 6 ghosted a segment that COMMITTED rir 4. Which
// values it carries, which stop paints each one and where the wheel must sit
// all live in `effortWheel.ts`; this file owns the pixels.
//
// The scale is display-only: ticks always REPORT the canonical rir
// (10..0, easy→hard) through onSelect, and only the printed numbers flip
// between RIR and RPE (10 − rir). "Easy" is a legitimate, sometimes
// deliberate choice — the monochrome ramp says intensity, never merit.
//
// Geometry lives in <MeterRow>, not here (MX-5 slice 1): the flexible ≥220×44
// area, the 8pt gap and the fixed right column are that primitive's job, so the
// hold/cardio clock can sit on the identical grid without copying the numbers.
// This file composes it and owns only the CONTENT of the two areas. The right
// column is a named slot (`trailing`) whose default is the Fail button, and the
// axis has one too (`axisTrailing`, default "Hard") — MX-5 turns that column
// into the action column of a timed set, and a default-shaped slot means the
// reps set doesn't move while that happens.
//
// Interaction: no horizontal hitSlop (adjacent segments would overlap),
// so the band is ≥44pt tall outright instead of 36+slop. Selected state
// is never colour-only: an inset ring in the stop's own `on` ink (the
// theme-global ink dies <3:1 on the hard stops of every family — a11y
// audit 2026-07-05) + accessibilityState. Segments are radios, not
// buttons: the band is a mutually-exclusive single-select, and the
// screen reader should say so. Per-stop `on` inks come AA-validated
// from the theme (effortRampContrast test, which also gates ring-vs-fill
// ≥3:1); numbers render solid — the mockup's 0.85 opacity would eat the
// validated margin.
//
// i18n note: this primitive calls t() itself — a documented exception to
// the caller-passes-strings helper contract, because Easy/Hard/Fail are
// intrinsic to the meter's semantics, fixed keys ×5 locales, not caller
// content (same stance as RecentPrsStrip). "Fail" is DELIBERATELY the
// same EN token in all 5 locales (Antenore, 2026-07-05 — like the RIR/RPE
// abbreviations); the localized FailSub underneath carries the semantics
// ("not completed" ×5), so the label is not a missing translation.

// The hardest value — rir 0, "nothing left". The collapsed variant is this
// one stop taking the whole band, which is why it needs no value of its own:
// it reports the same canonical rir the wheel's last tick reports.
const HARD_RIR = 0;

export type EffortMeterProps = {
  theme: UiTheme;
  /** The athlete's display scale (Profile.effortScale). Labels only. */
  scale: EffortScale;
  /** Canonical rir of the logged set, null when still pending. Every value
   *  0..10 has a tick of its own now; a half-step (2.5) matches none and
   *  selects nothing, exactly as it matched no segment before. Ignored while
   *  `failed`. */
  value: number | null;
  failed: boolean;
  /** Reports the canonical rir (10..0) of the tapped tick. */
  onSelect: (rir: number) => void;
  onFail: () => void;
  disabled?: boolean;
  /** accessibilityHint for the segments — the host says what a tap DOES
   *  ("also logs the set" vs "updates the effort"), which the visual band
   *  can't. i18n-agnostic: the caller passes the localized string. */
  segmentHint?: string;
  /** accessibilityHint for the Fail button, same contract as segmentHint. */
  failHint?: string;
  /** Optional node centered in the axis row, BETWEEN the Easy and Hard labels
   *  (AP-0j: the effort question lives here — "tra easy e hard, in mezzo").
   *  When absent the row stays Easy … Hard. */
  axisCenter?: ReactNode;
  /** Optional node in the axis row's LEFT slot. Defaults to the "Easy"
   *  end-label. The compact (timed/cardio) variant puts the single word
   *  "Effort" here instead: on a hold "how many reps in reserve" has no
   *  answer, so the long question and both end-labels give way (MX-5 D10/Q2 —
   *  direction still reads from the numbers climbing 6− → 10, which is shape,
   *  not colour, so the a11y floor holds). */
  axisLeading?: ReactNode;
  /** Optional node in the axis row's RIGHT slot, over the meter's fixed column.
   *  Defaults to the "Hard" end-label. MX-5 puts the running clock here (the
   *  axis stops naming the ends while a timer owns the row). */
  axisTrailing?: ReactNode;
  /** Optional node in the meter row's fixed right column. Defaults to the Fail
   *  button — ink and a11y unchanged. MX-5 hands this column to the
   *  timed set's action (start chip → elapsed time); when a caller fills it,
   *  Fail is not rendered (D2: on a hold, going to failure IS stop 10, so a
   *  second road to the same datum would be one road too many). */
  trailing?: ReactNode;
  /** Drop the fixed column entirely and give the whole row to the band. Not the
   *  same as passing no `trailing` (which falls back to Fail): this says the row
   *  has no second job right now. A running hold/cardio timer uses it — the band
   *  is then the only control on the row, so it takes every point of width,
   *  which is also what rescues the segments on a narrow screen (DS-27). */
  trailingHidden?: boolean;
  /** Collapse the wheel into ONE wide segment at the hard end, still
   *  reporting the canonical rir 0 (LG-19). For a set the athlete has marked
   *  AMRAP: "how many reps did you have left" has an answer there, and the
   *  answer is already known to be zero — asking it back is a question with one
   *  legal reply, so the scale stops offering a choice. Same band, one tap,
   *  same write path (there is no second control anywhere: that is the whole
   *  point). Its sibling case is the compact variant's `axisLeading`, where the
   *  question has NO answer instead of a known one — a plank has no reps.
   *  End-labels give way with it: "Easy … Hard" names the ends of a continuum,
   *  and a one-stop band has none. Fail does NOT: passing this changes the band
   *  only, so "I stopped with nothing left" and "I tried and it didn't move"
   *  stay two different, both-recordable facts (Antenore 2026-07-23). */
  collapsed?: boolean;
  /** The word printed on the collapsed segment, in place of the raw scale
   *  number (LG-19 smoke, Antenore 2026-07-23: "al posto dello 0 metti Done").
   *  "0"/"10" is the RIR/RPE value the tap still commits, but as a label it is
   *  jargon; on a one-stop band the athlete reads a verb, not a number. Only
   *  consulted when `collapsed` — the full ramp always prints its numbers. */
  collapsedLabel?: string;
  /** The PRESCRIBED effort as canonical rir — drawn as a GHOST on its segment,
   *  never as a selection (PE-22 D2 / AP-1). Two sources, both legitimate and
   *  both already canonical rir by the time they get here: a program that chose
   *  effort as its load axis, and a REPEATED session, where the last time's
   *  effort IS the target (Antenore 2026-08-26: "il valore del precedente al
   *  REPEAT diventa la prescrizione"). null = nothing prescribed.
   *
   *  Drawn by `targetMark` below, which carries the geometry and the reason for
   *  it. Ignored while `collapsed`: an AMRAP band has one stop whose value is
   *  already known, so there is no target left to point at (Antenore, same day:
   *  "AMRAP resta AMRAP, no cue").
   *
   *  Deliberately NOT paired with a caption: the target is the mark and nothing
   *  else — "non voglio aggiungere righe al logger". The a11y label carries it
   *  for anyone who can't see the mark; that costs no space. */
  targetRir?: number | null;
  /** PR gold pulse (AP-0b): when flashKey increments, the segment matching
   *  flashRir flashes the theme's pr/prFill pair for ~900ms. Transient
   *  celebration only — the durable PR signal stays the row's trophy tag
   *  (gold = status, never part of the ramp). */
  flashRir?: number | null;
  flashKey?: number;
};

export function EffortMeter({
  theme,
  scale,
  value,
  failed,
  onSelect,
  onFail,
  disabled = false,
  segmentHint,
  failHint,
  axisCenter,
  axisLeading,
  axisTrailing,
  trailing,
  trailingHidden = false,
  collapsed = false,
  collapsedLabel,
  targetRir = null,
  flashRir = null,
  flashKey = 0,
}: EffortMeterProps) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const selectedRir = failed ? null : value;
  // The Fail box takes the effort ramp's HARDEST tone (themed, per-family) —
  // a fail is the extreme of effort, not an error, so it's coloured, not grey
  // (Antenore 2026-07-20). Fill + ink ride inline off the last stop, like the
  // segments. Fallback keeps types happy if a theme ever ships an empty ramp.
  const hardStop = theme.effortRamp[theme.effortRamp.length - 1] ?? {
    fill: theme.palette.surface2,
    on: theme.palette.ink,
  };

  // PR pulse — one Animated opacity, re-armed on every flashKey bump so a
  // second PR on the same segment still flashes. Opacity-only → native driver.
  const flashOpacity = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    if (!flashKey) return;
    flashOpacity.setValue(1);
    Animated.timing(flashOpacity, {
      toValue: 0,
      duration: 900,
      useNativeDriver: true,
    }).start();
  }, [flashKey, flashOpacity]);
  const flashOn = flashKey ? flashRir : null;
  // The ghost never survives the fact: once the athlete has answered, the target
  // has nothing left to suggest. `failed` has to be named explicitly — it is an
  // answer, but it empties `selectedIdx` above, so a "not selected" test would
  // quietly let the ghost through on the one set that went worst. The guard
  // lives HERE, not in the caller's `pending` gate: a second mount that forgets
  // that gate must not be able to resurrect the phantom.
  const targetOn = collapsed || failed ? null : targetRir;

  // ── The wheel's position ────────────────────────────────────────────────
  // Same shape as <ExerciseVisualPager>: the ticks have no width until the
  // measurement lands, so scrolling before it scrolls the old geometry. An
  // effect, not the layout callback, so it runs after the commit that sizes
  // the content.
  const scrollRef = useRef<ScrollView>(null);
  const [bandWidth, setBandWidth] = useState(0);
  const reducedMotion = useReducedMotion();
  const hasPositioned = useRef(false);

  const onBandLayout = useCallback((e: LayoutChangeEvent) => {
    setBandWidth(Math.round(e.nativeEvent.layout.width));
  }, []);

  useEffect(() => {
    if (collapsed || bandWidth <= 0) return;
    // First measure: the FACT wins if there is one — reopening a logged set
    // shows what the athlete did, not what was asked of them. After that only
    // a new prescription moves the wheel.
    // ⛔ `value` is deliberately NOT a dependency. Re-centring on every
    // selection would slide the band out from under the thumb that just landed
    // on it, on the hottest path in the app.
    const focus = hasPositioned.current ? targetRir : (value ?? targetRir);
    // The first placement is INSTANT: there is nothing to animate from, and a
    // wheel seen sliding into position on every mount would read as motion
    // for its own sake (and never finished under the headless bench, which is
    // how this was caught). Only a prescription that changes later travels —
    // and not under reduced motion.
    const animated = hasPositioned.current && !reducedMotion;
    hasPositioned.current = true;
    scrollRef.current?.scrollTo({
      x: effortWheelOffset(focus ?? null, bandWidth),
      animated,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bandWidth, targetRir, collapsed, reducedMotion]);

  // One tick renderer for BOTH containers — the wheel and the collapsed band
  // — so there is exactly one commit gesture to keep honest.
  const renderTick = (rir: number) => (
    <EffortTick
      key={rir}
      theme={theme}
      rir={rir}
      scale={scale}
      wide={collapsed}
      labelOverride={collapsed ? collapsedLabel : undefined}
      selected={selectedRir === rir}
      isTarget={targetOn === rir && selectedRir !== rir}
      flashing={flashOn === rir}
      flashOpacity={flashOpacity}
      disabled={disabled}
      hint={segmentHint}
      onSelect={onSelect}
    />
  );

  return (
    <View>
      <View style={styles.axis}>
        {/* End-labels name the ends of the ramp, so they hold exactly as long
            as the ramp does: collapsed, there is one stop and no "easy" end to
            point at. Slot-passed nodes still win — a caller that fills the slot
            has said what belongs there. */}
        {axisLeading ??
          (collapsed ? null : (
            <Text style={styles.axisLabel}>
              {t("mobile.logger.effortMeterEasy", { defaultValue: "Easy" })}
            </Text>
          ))}
        {/* Center slot (AP-0j) — the effort question sits between Easy and Hard,
            in a flex spacer so the labels stay pinned to the edges even with no
            center node. The compact variant passes a leading node and NO center
            one: there the spacer would be a second flex:1 sibling and would eat
            half the row the word needs for its longest locale, so it collapses
            instead (a11y audit 2026-07-22). */}
        {axisCenter == null && axisLeading != null ? null : (
          <View style={styles.axisCenter}>{axisCenter}</View>
        )}
        {axisTrailing ??
          (collapsed ? null : (
            <Text style={styles.axisLabel}>
              {t("mobile.logger.effortMeterHard", { defaultValue: "Hard" })}
            </Text>
          ))}
      </View>
      <MeterRow
        theme={theme}
        accessibilityRole="radiogroup"
        trailing={
          trailingHidden ? undefined : (
          trailing ?? (
            <Pressable
              accessibilityRole="radio"
              accessibilityLabel={t("mobile.logger.effortMeterFail", {
                defaultValue: "Fail",
              })}
              accessibilityHint={failHint}
              accessibilityState={{
                selected: failed,
                checked: failed,
                disabled,
              }}
              // Same reason as the tick: RNW drops accessibilityState.
              aria-checked={failed}
              disabled={disabled}
              onPress={onFail}
              style={({ pressed }) => [
                styles.fail,
                { backgroundColor: hardStop.fill },
                failed && styles.failSelected,
                failed && { borderColor: hardStop.on },
                pressed && !disabled && styles.pressed,
                disabled && styles.disabled,
              ]}
            >
              <Text style={[styles.failTitle, { color: hardStop.on }]}>
                {/* ✕ prefix when selected: a SHAPE cue beyond the ring, so
                    selection doesn't read by colour alone. Matches the
                    timeline's ✕. */}
                {failed ? "✕ " : ""}
                {t("mobile.logger.effortMeterFail", { defaultValue: "Fail" })}
              </Text>
            </Pressable>
            )
          )
        }
      >
        {/* One tick renderer, two containers: collapsed renders the hard
            value ALONE through the very same function — same fill, same
            validated `on` ink, same onSelect(rir), same radio semantics. A
            second render branch here would be a second commit gesture to keep
            honest. Only the box around it differs: a wheel that scrolls, or a
            single stop taking the width. */}
        {collapsed ? (
          <View style={styles.band}>{renderTick(HARD_RIR)}</View>
        ) : (
          <ScrollView
            ref={scrollRef}
            horizontal
            onLayout={onBandLayout}
            // ON, his call (3/09, from the a11y audit): at rest the only sign
            // that the wheel has more to the left is the sliver of the "5"
            // tick — most of a tick on a ≥360dp phone, but 4pt at 280dp
            // (Display size at max, i.e. exactly the eyes the floor protects),
            // where the old "4+" at least said "or more". The native bar does
            // not fix rest discoverability (it shows while scrolling), it
            // accompanies it; a resting cue would be a new element, his to
            // draw.
            showsHorizontalScrollIndicator
            // ⛔ LG-66 — NOT a default, and not optional. The athlete types
            // reps/weight in the fields directly above (FocusPanel mounts
            // FocusPanelInputs at :324 and this meter at :395), so the keyboard
            // is up on the hottest path in the app. RN's default here is
            // "never": the first touch after typing is consumed to dismiss the
            // keyboard and NEVER reaches the tick — the tap that IS the
            // completion gesture. Antenore, 2026-09-04: «può far erroneamente
            // pensare che ho markato un set come fatto, ma in realtà non lo è».
            // Before the wheel this hole did not exist, because the ticks were
            // flat Pressables inside the screen's ScrollView, which declares
            // "handled" (TimelineLoggerScreen.tsx:235) — the nesting is what
            // introduced it, so the nested view has to say it too.
            // ⚠️ This does NOT touch scroll-vs-tap: that arbitration is the
            // native responder's (a drag past the slop steals the touch and the
            // tick's onPress never fires), which is why scrolling the wheel to
            // a far value cannot commit one by accident.
            keyboardShouldPersistTaps="handled"
            // Snapping on the tick, not on the page: the wheel comes to rest
            // with whole values under the window, never half of two.
            snapToInterval={EFFORT_TICK_WIDTH}
            decelerationRate="fast"
            style={styles.band}
            contentContainerStyle={styles.wheel}
          >
            {WHEEL_RIR.map((rir) => renderTick(rir))}
          </ScrollView>
        )}
      </MeterRow>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    axis: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s2,
      marginBottom: s.s2,
    },
    axisLabel: { color: p.ink3, fontSize: t.sizes.xs },
    // Grows to fill the middle; centers the question between Easy and Hard.
    axisCenter: { flex: 1, alignItems: "center" },
    // Fills MeterRow's leading area, which owns the 44 height floor. As the
    // wheel's OUTER box it is what clips the ticks to the rounded ends.
    band: {
      flex: 1,
      flexDirection: "row",
      borderRadius: r.r3,
      overflow: "hidden",
    },
    // The wheel's content container. ⚠️ It carries the height floor and the
    // row direction TOGETHER, and that pairing is load-bearing: MeterRow's
    // areas must stay row containers or a child's `flex: 1` silently starts
    // meaning "take all the HEIGHT" (the v4.3.1-dev cardio regression). Height
    // comes from here + stretch, so the ticks still grow with font scale
    // instead of being pinned to 44.
    wheel: {
      flexDirection: "row",
      minHeight: METER_ROW_MIN_HEIGHT,
      alignItems: "stretch",
    },
    // Selected ring on the Fail button — a 2px ring whose colour is the ramp
    // stop's own `on` ink (applied inline in the render, so it stays legible on
    // the themed fill), paired with the ✕ glyph so selection reads by weight +
    // shape, not colour. Segments get their ring inline from the stop's `on` ink.
    failSelected: {
      borderWidth: 2,
    },
    // Fills MeterRow's fixed column (the width + no-shrink live there, in one
    // place, where the axis clock reads the same number).
    // A fail is a normal training outcome, not an error (Antenore 2026-07-20):
    // the box takes the effort ramp's HARDEST tone (themed, per-family, applied
    // inline in the render) — you gave everything, the extreme of effort, never
    // alarm red. The ✕ selected glyph + a 2px ring in the stop's `on` ink carry
    // selection by shape. Narrow: just "Fail" now (the "not completed" sub was
    // dropped — Antenore, it was too wide and pushed the box off the row).
    fail: {
      flex: 1,
      paddingHorizontal: s.s2,
      borderRadius: r.r3,
      alignItems: "center",
      justifyContent: "center",
    },
    failTitle: { fontSize: t.sizes.sm, fontWeight: "700" },
    pressed: { opacity: 0.7 },
    disabled: { opacity: 0.5 },
  });
}
